import React, { useEffect, useRef, useState } from "react";
import { Bell, X, AlertTriangle, CalendarClock, FileText, Percent, Settings2 } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ICON = { due: CalendarClock, overdue: AlertTriangle, statement: FileText, promo: Percent };

export default function NotificationBell({ compact }) {
  const t = (id) => (compact ? `mobile-${id}` : id);
  const [data, setData] = useState({ items: [], count: 0, lead_days: 3 });
  const [open, setOpen] = useState(false);
  const [lead, setLead] = useState(3);
  const ref = useRef(null);

  const load = () => api.get("/notifications").then(r => { setData(r.data); setLead(r.data.lead_days); }).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc); return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const dismiss = async (key) => { await api.post("/notifications/dismiss", { key }); load(); };
  const saveLead = async (v) => {
    const n = Math.max(0, Math.min(30, parseInt(v, 10) || 0)); setLead(n);
    await api.put("/payoff-plan", { notify_lead_days: n }); toast.success(`Reminders start ${n} day${n === 1 ? "" : "s"} ahead`); load();
  };

  return (
    <div ref={ref} className="relative" data-testid={t("notification-bell")}>
      <button onClick={() => setOpen(o => !o)} data-testid={t("notification-bell-btn")} aria-label="Notifications"
        className={`relative flex items-center gap-2 rounded-xl border border-border hover:bg-accent transition ${compact ? "p-2" : "w-full px-3 py-2 text-sm"}`}>
        <Bell className="h-4 w-4" />
        {!compact && <span className="flex-1 text-left">Reminders</span>}
        {data.count > 0 && (
          <span data-testid={t("notification-count")} className={`${compact ? "absolute -top-1 -right-1" : ""} h-5 min-w-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center`}>{data.count}</span>
        )}
      </button>
      {open && (
        <div data-testid={t("notification-panel")} className={`absolute z-50 w-80 sm:w-96 rounded-2xl border border-border bg-card shadow-2xl ${compact ? "right-0 top-12" : "left-0 bottom-12"}`}>
          <div className="p-3 border-b border-border flex items-center justify-between">
            <div className="font-display font-bold text-sm">Reminders</div>
            <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Settings2 className="h-3 w-3" /> alert
              <input type="number" min={0} max={30} value={lead} onChange={e => setLead(e.target.value)} onBlur={e => saveLead(e.target.value)} data-testid={t("notification-lead-input")}
                className="w-12 px-1.5 py-0.5 rounded-md border border-border bg-background font-mono text-right text-foreground" /> days ahead</label>
          </div>
          <ul className="max-h-96 overflow-y-auto thin-scroll divide-y divide-border/60">
            {data.items.length === 0 && <li className="p-6 text-center text-sm text-muted-foreground" data-testid={t("notification-empty")}>You're all caught up — nothing due in the next {lead} day{lead === 1 ? "" : "s"}.</li>}
            {data.items.map(i => {
              const Icon = ICON[i.type] || Bell;
              const tone = i.severity === "high" ? "text-rose-500 bg-rose-500/10" : i.type === "promo" ? "text-violet-400 bg-violet-500/10" : "text-amber-500 bg-amber-500/10";
              return (
                <li key={i.key} className="p-3 flex items-start gap-3" data-testid={t(`notification-${i.key.split(":")[0]}-${i.key.split(":")[1]}`)}>
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${tone}`}><Icon className="h-4 w-4" /></div>
                  <Link to={i.link} onClick={() => setOpen(false)} className="flex-1 min-w-0">
                    <div className="text-sm font-semibold leading-tight">{i.title}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{i.detail}</div>
                  </Link>
                  <button onClick={() => dismiss(i.key)} title="Dismiss" data-testid={t(`notification-dismiss-${i.key.split(":")[1]}`)} className="p-1 rounded-md hover:bg-accent text-muted-foreground"><X className="h-3.5 w-3.5" /></button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
